import { useEffect } from 'react';
import './About.css';

const About = () => {
  // Hook para animaciones
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in--visible');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Datos de la empresa
  const companyData = {
    founded: '2004',
    experience: '20',
    location: 'Ciudad de México',
    specialization: 'Tecnologías de la Información y Comunicaciones'
  };

  // Sectores de experiencia
  const sectors = [
    {
      icon: '🏛️',
      name: 'Gobierno',
      description: 'Implementación de sistemas de gestión para entidades gubernamentales',
      projects: '50+'
    },
    {
      icon: '🚗',
      name: 'Automotriz',
      description: 'Soluciones especializadas para la industria automotriz',
      projects: '30+'
    },
    {
      icon: '🏦',
      name: 'Financiero',
      description: 'Sistemas de gestión y consultoría para el sector financiero',
      projects: '40+'
    },
    {
      icon: '⚡',
      name: 'Petróleo',
      description: 'Consultoría especializada para la industria petrolera',
      projects: '25+'
    }
  ];

  // Valores de la empresa
  const values = [
    {
      icon: '🎯',
      title: 'Excelencia',
      description: 'Comprometidos con la calidad y la mejora continua en todos nuestros servicios'
    },
    {
      icon: '🤝',
      title: 'Confianza',
      description: 'Construimos relaciones duraderas basadas en la transparencia y la honestidad'
    },
    {
      icon: '💡',
      title: 'Innovación',
      description: 'Adoptamos las mejores prácticas y tecnologías para mantenernos a la vanguardia'
    },
    {
      icon: '🚀',
      title: 'Resultados',
      description: 'Orientados a entregar soluciones que generen valor real para nuestros clientes'
    }
  ];

  // Certificaciones y normas
  const certifications = [
    { name: 'ISO 9001', type: 'Gestión de Calidad' },
    { name: 'ISO 14001', type: 'Gestión Ambiental' },
    { name: 'ISO 27001', type: 'Seguridad de la Información' },
    { name: 'ISO 45001', type: 'Seguridad y Salud Ocupacional' },
    { name: 'COBIT', type: 'Gobierno de TI' },
    { name: 'ITIL', type: 'Gestión de Servicios TI' }
  ];

  // Metodologías
  const methodologies = [
    'Gestión de Proyectos (PMI)',
    'Metodologías Ágiles (Scrum, Kanban)',
    'Six Sigma',
    'Lean Manufacturing',
    'PRINCE2',
    'DevOps'
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero__content fade-in">
            <span className="about-hero__eyebrow">Fundada en 2004</span>
            <h1 className="about-hero__title">
              ¿Quiénes <span className="about-hero__title-gradient">Somos?</span>
            </h1>
            <p className="about-hero__subtitle">
              20 años transformando organizaciones a través de la excelencia en servicios y consultoría profesional
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="about-story">
        <div className="container">
          <div className="about-story__grid">
            <div className="about-story__content fade-in">
              <span className="about-story__eyebrow">Nuestra Historia</span>
              <h2 className="about-story__title">Nuestra Historia</h2>
              <p className="about-story__text">
                Somos una empresa de <strong>Servicios y Consultoría Profesional</strong>, fundada en el año <strong>2004</strong> en la Ciudad de México. Durante estas dos décadas, hemos construido una sólida reputación basada en la excelencia y el compromiso con nuestros clientes.
              </p>
              <p className="about-story__text">
                Contamos con una amplia experiencia en la <strong>implementación de Sistemas de Gestión</strong> basados en Normas Nacionales e Internacionales, reconociendo que estas representan las mejores prácticas de cada industria a nivel global.
              </p>
              <p className="about-story__text">
                Nuestro personal experto asesora a organizaciones de diferentes sectores, siendo especialistas en <strong>Procesos de Tecnologías de la Información y Comunicaciones</strong>, manejando proyectos llave en mano para obtener certificaciones nacionales e internacionales.
              </p>
            </div>
            <div className="about-story__visual fade-in">
              <div className="about-story__stats">
                <div className="about-stat">
                  <span className="about-stat__number">{companyData.experience}</span>
                  <span className="about-stat__label">Años de Experiencia</span>
                </div>
                <div className="about-stat">
                  <span className="about-stat__number">500+</span>
                  <span className="about-stat__label">Proyectos Exitosos</span>
                </div>
                <div className="about-stat">
                  <span className="about-stat__number">4</span>
                  <span className="about-stat__label">Sectores Especializados</span>
                </div>
                <div className="about-stat">
                  <span className="about-stat__number">98%</span>
                  <span className="about-stat__label">Satisfacción del Cliente</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="about-sectors">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-header__eyebrow">Experiencia</span>
            <h2 className="section-header__title">Sectores de Experiencia</h2>
            <p className="section-header__subtitle">
              Especialistas en diversos sectores industriales con proyectos exitosos
            </p>
          </div>
          <div className="sectors-grid">
            {sectors.map((sector, index) => (
              <div key={index} className="sector-card fade-in">
                <div className="sector-card__icon">{sector.icon}</div>
                <h3 className="sector-card__name">{sector.name}</h3>
                <p className="sector-card__description">{sector.description}</p>
                <div className="sector-card__projects">{sector.projects} proyectos</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about-values">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-header__eyebrow">Lo que nos define</span>
            <h2 className="section-header__title">Nuestros Valores</h2>
            <p className="section-header__subtitle">
              Los principios que guían cada uno de nuestros proyectos y decisiones
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card fade-in">
                <div className="value-card__icon">{value.icon}</div>
                <h3 className="value-card__title">{value.title}</h3>
                <p className="value-card__description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="about-expertise">
        <div className="container">
          <div className="expertise-grid">
            <div className="expertise-content fade-in">
              <h2 className="expertise-content__title">Nuestra Expertise</h2>
              <p className="expertise-content__text">
                Como especialistas en Tecnologías de la Información y Comunicaciones, dominamos las metodologías y frameworks más reconocidos a nivel internacional.
              </p>
              
              <div className="expertise-section">
                <h3 className="expertise-section__title">Certificaciones y Normas</h3>
                <div className="certifications-list">
                  {certifications.map((cert, index) => (
                    <div key={index} className="certification-item">
                      <span className="certification-name">{cert.name}</span>
                      <span className="certification-type">{cert.type}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="expertise-section">
                <h3 className="expertise-section__title">Metodologías</h3>
                <div className="methodologies-list">
                  {methodologies.map((methodology, index) => (
                    <div key={index} className="methodology-item">
                      <span className="methodology-bullet"></span>
                      <span className="methodology-text">{methodology}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="expertise-visual fade-in">
              <div className="expertise-visual__content">
                <div className="expertise-visual__text">
                  <h4>Desde 2004</h4>
                  <p>Liderando la transformación organizacional en México</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="about-mission">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-card fade-in">
              <div className="mission-card__icon">🎯</div>
              <h3 className="mission-card__title">Misión</h3>
              <p className="mission-card__text">
                Proporcionar servicios de consultoría y soluciones tecnológicas de clase mundial que permitan a nuestros clientes alcanzar la excelencia operacional y obtener ventajas competitivas sostenibles en sus respectivos mercados.
              </p>
            </div>
            <div className="mission-card fade-in">
              <div className="mission-card__icon">🌟</div>
              <h3 className="mission-card__title">Visión</h3>
              <p className="mission-card__text">
                Ser reconocidos como la empresa líder en México en servicios de consultoría y implementación de sistemas de gestión, siendo el socio estratégico preferido para la transformación organizacional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <div className="about-cta__content fade-in">
            <h2 className="about-cta__title">¿Listo para transformar tu organización?</h2>
            <p className="about-cta__subtitle">
              Únete a las más de 500 empresas que han confiado en nuestra experiencia de 20 años
            </p>
            <div className="about-cta__buttons">
              <button className="btn btn--primary btn--large">
                Conocer Servicios
              </button>
              <button className="btn btn--ghost btn--large">
                Contactar Ahora
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;